import React, { useState, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  VStack,
  HStack,
  Switch,
  FormHelperText,
  useToast,
  Divider,
  Heading,
  Text,
  Tag,

  InputGroup,
  InputLeftElement,
  Icon,
} from '@chakra-ui/react'
import { useMutation, useQueryClient } from 'react-query'
import { LinkIcon, AtSignIcon } from '@chakra-ui/icons'
import { api } from '../services/api'

interface UserProfile {
  const_id: string
  username?: string
  email?: string
  name?: string
  picture_url?: string
  nickname?: string
  bio?: string
  location?: string
  website?: string
  company?: string
  job_title?: string
  programming_languages?: string[]
  experience_level?: string
  linkedin_url?: string
  github_url?: string
  twitter_url?: string
  profile_visibility?: string
  show_real_name?: boolean
  show_email?: boolean
  show_location?: boolean
  show_stats?: boolean
}

interface ProfileEditorProps {
  isOpen: boolean
  onClose: () => void
  profile: UserProfile
  onUpdate: () => void
}

interface ProfileUpdateData {
  bio?: string
  location?: string
  website?: string
  company?: string
  job_title?: string
  programming_languages?: string[]
  experience_level?: string
  linkedin_url?: string
  github_url?: string
  twitter_url?: string
  profile_visibility?: string
  show_real_name?: boolean
  show_email?: boolean
  show_location?: boolean
  show_stats?: boolean
}

const ProfileEditor: React.FC<ProfileEditorProps> = ({
  isOpen,
  onClose,
  profile,
  onUpdate,
}) => {
  const [formData, setFormData] = useState<ProfileUpdateData>({})
  const [languageInput, setLanguageInput] = useState('')
  const toast = useToast()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (profile) {
      setFormData({
        bio: profile.bio || '',
        location: profile.location || '',
        website: profile.website || '',
        company: profile.company || '',
        job_title: profile.job_title || '',
        programming_languages: profile.programming_languages || [],
        experience_level: profile.experience_level || '',
        linkedin_url: profile.linkedin_url || '',
        github_url: profile.github_url || '',
        twitter_url: profile.twitter_url || '',
        profile_visibility: profile.profile_visibility || 'public',
        show_real_name: profile.show_real_name !== false,
        show_email: profile.show_email === true,
        show_location: profile.show_location !== false,
        show_stats: profile.show_stats !== false,
      })
    }
  }, [profile])

  const updateProfileMutation = useMutation(
    (data: ProfileUpdateData) => api.put('/user/profile', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('userProfile')
        onUpdate()
        toast({
          title: 'Profile updated',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
      },
      onError: (error) => {
        toast({
          title: 'Update failed',
          description: 'Failed to update profile. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      },
    }
  )

  const handleInputChange = (field: keyof ProfileUpdateData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleAddLanguage = () => {
    if (languageInput.trim() && !formData.programming_languages?.includes(languageInput.trim())) {
      const newLanguages = [...(formData.programming_languages || []), languageInput.trim()]
      handleInputChange('programming_languages', newLanguages)
      setLanguageInput('')
    }
  }

  const handleRemoveLanguage = (language: string) => {
    const newLanguages = formData.programming_languages?.filter(lang => lang !== language) || []
    handleInputChange('programming_languages', newLanguages)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    updateProfileMutation.mutate(formData)
  }

  const handleClose = () => {
    if (!updateProfileMutation.isLoading) {
      onClose()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <form onSubmit={handleSubmit}>
          <ModalHeader>Edit Profile</ModalHeader>
          <ModalCloseButton />
          
          <ModalBody pb={6} maxH="calc(90vh - 120px)" overflowY="auto">
            <VStack spacing={6} align="stretch">
              {/* Basic Information */}
              <VStack spacing={4} align="stretch">
                <Heading size="sm" color="gray.600">Basic Information</Heading>
                
                <FormControl>
                  <FormLabel>Bio</FormLabel>
                  <Textarea
                    value={formData.bio || ''}
                    onChange={(e) => handleInputChange('bio', e.target.value)}
                    placeholder="Tell us about yourself..."
                    rows={3}
                  />
                  <FormHelperText>A brief description about yourself and your interests</FormHelperText>
                </FormControl>

                <HStack spacing={4}>
                  <FormControl>
                    <FormLabel>Location</FormLabel>
                    <Input
                      value={formData.location || ''}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      placeholder="City, Country"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Experience Level</FormLabel>
                    <Select
                      value={formData.experience_level || ''}
                      onChange={(e) => handleInputChange('experience_level', e.target.value)}
                    >
                      <option value="">Select level</option>
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                      <option value="expert">Expert</option>
                    </Select>
                  </FormControl>
                </HStack>

                <HStack spacing={4}>
                  <FormControl>
                    <FormLabel>Company</FormLabel>
                    <Input
                      value={formData.company || ''}
                      onChange={(e) => handleInputChange('company', e.target.value)}
                      placeholder="Company name"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Job Title</FormLabel>
                    <Input
                      value={formData.job_title || ''}
                      onChange={(e) => handleInputChange('job_title', e.target.value)}
                      placeholder="Your role"
                    />
                  </FormControl>
                </HStack>

                <FormControl>
                  <FormLabel>Programming Languages</FormLabel>
                  <HStack spacing={2} mb={2} flexWrap="wrap">
                    {formData.programming_languages?.map((lang) => (
                      <Tag
                        key={lang}
                        size="md"
                        borderRadius="full"
                        variant="solid"
                        colorScheme="blue"
                        cursor="pointer"
                        onClick={() => handleRemoveLanguage(lang)}
                      >
                        {lang} ×
                      </Tag>
                    ))}
                  </HStack>
                  <HStack>
                    <Input
                      value={languageInput}
                      onChange={(e) => setLanguageInput(e.target.value)}
                      placeholder="Add a programming language"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddLanguage())}
                    />
                    <Button onClick={handleAddLanguage} size="sm">Add</Button>
                  </HStack>
                  <FormHelperText>Click on tags to remove them</FormHelperText>
                </FormControl>
              </VStack>

              <Divider />

              {/* Links */}
              <VStack spacing={4} align="stretch">
                <Heading size="sm" color="gray.600">Links</Heading>
                
                <FormControl>
                  <FormLabel>Website</FormLabel>
                  <InputGroup>
                    <InputLeftElement>
                      <LinkIcon color="gray.400" />
                    </InputLeftElement>
                    <Input
                      value={formData.website || ''}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                      placeholder="https://yourwebsite.com"
                    />
                  </InputGroup>
                </FormControl>

                <FormControl>
                  <FormLabel>LinkedIn</FormLabel>
                  <InputGroup>
                    <InputLeftElement>
                      <Text color="gray.400" fontSize="sm">in</Text>
                    </InputLeftElement>
                    <Input
                      value={formData.linkedin_url || ''}
                      onChange={(e) => handleInputChange('linkedin_url', e.target.value)}
                      placeholder="https://linkedin.com/in/username"
                    />
                  </InputGroup>
                </FormControl>

                <FormControl>
                  <FormLabel>GitHub</FormLabel>
                  <InputGroup>
                    <InputLeftElement>
                      <AtSignIcon color="gray.400" />
                    </InputLeftElement>
                    <Input
                      value={formData.github_url || ''}
                      onChange={(e) => handleInputChange('github_url', e.target.value)}
                      placeholder="https://github.com/username"
                    />
                  </InputGroup>
                </FormControl>

                <FormControl>
                  <FormLabel>Twitter</FormLabel>
                  <InputGroup>
                    <InputLeftElement>
                      <Text color="gray.400" fontSize="sm">@</Text>
                    </InputLeftElement>
                    <Input
                      value={formData.twitter_url || ''}
                      onChange={(e) => handleInputChange('twitter_url', e.target.value)}
                      placeholder="https://twitter.com/username"
                    />
                  </InputGroup>
                </FormControl>
              </VStack>

              <Divider />

              {/* Privacy Settings */}
              <VStack spacing={4} align="stretch">
                <Heading size="sm" color="gray.600">Privacy Settings</Heading>
                
                <FormControl>
                  <FormLabel>Profile Visibility</FormLabel>
                  <Select
                    value={formData.profile_visibility || 'public'}
                    onChange={(e) => handleInputChange('profile_visibility', e.target.value)}
                  >
                    <option value="public">Public - Anyone can view</option>
                    <option value="private">Private - Only you can view</option>
                  </Select>
                </FormControl>

                <VStack spacing={3} align="stretch">
                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" flex="1">
                      Show real name
                    </FormLabel>
                    <Switch
                      isChecked={formData.show_real_name || false}
                      onChange={(e) => handleInputChange('show_real_name', e.target.checked)}
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" flex="1">
                      Show email address
                    </FormLabel>
                    <Switch
                      isChecked={formData.show_email || false}
                      onChange={(e) => handleInputChange('show_email', e.target.checked)}
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" flex="1">
                      Show location
                    </FormLabel>
                    <Switch
                      isChecked={formData.show_location || false}
                      onChange={(e) => handleInputChange('show_location', e.target.checked)}
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" flex="1">
                      Show statistics
                    </FormLabel>
                    <Switch
                      isChecked={formData.show_stats || false}
                      onChange={(e) => handleInputChange('show_stats', e.target.checked)}
                    />
                  </FormControl>
                </VStack>
              </VStack>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleClose} disabled={updateProfileMutation.isLoading}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              type="submit"
              isLoading={updateProfileMutation.isLoading}
              loadingText="Updating..."
            >
              Update Profile
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}

export default ProfileEditor 